import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Event } from '../types';
import { Loader, ArrowLeft, Calendar, MapPin, Clock, Share2 } from 'lucide-react';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'events', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setEvent({ id: docSnap.id, ...docSnap.data() } as Event);
        } else {
          console.log("No such event!");
        }
      } catch (error) {
        console.error("Error fetching event:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen pt-32 flex justify-center">
        <Loader className="animate-spin" />
    </div>
  );

  if (!event) return (
    <div className="min-h-screen pt-32 text-center px-4">
        <h2 className="font-heading text-2xl font-bold mb-4">Event Not Found</h2>
        <button onClick={() => navigate('/events')} className="text-sm border-b border-black pb-1 hover:opacity-50">Back to Events</button>
    </div>
  );

  return (
    <article className="min-h-screen pt-24 pb-20">
      
      {/* Hero Image */}
      <div className="w-full h-[50vh] md:h-[60vh] relative mb-12">
        <img
          src={event.imageUrl || '/assets/placeholder.svg'}
          alt={event.title}
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
          }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-0 left-0 w-full p-4 md:p-12 text-white">
            <div className="max-w-4xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-white text-black px-3 py-1 text-xs font-bold uppercase tracking-widest mb-6">
                    <span className={`w-2 h-2 rounded-full ${event.status === 'upcoming' ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                    {event.status}
                </div>
                <h1 className="font-heading text-4xl md:text-6xl font-bold leading-tight mb-2 uppercase">
                    {event.title}
                </h1>
                {event.subtitle && (
                    <p className="font-heading font-bold text-lg md:text-xl text-gray-300 md:max-w-2xl tracking-wide uppercase">
                        {event.subtitle}
                    </p>
                )}
            </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-12">
        
        {/* Left: Details Sidebar */}
        <div className="md:col-span-1 space-y-8">
            <div className="bg-gray-50 p-6 border border-gray-100">
                <h3 className="font-heading font-bold text-lg mb-6 uppercase border-b border-black pb-2">Event Details</h3>
                
                <div className="space-y-6">
                    <div>
                        <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">
                            <Calendar size={14} /> Date
                        </div>
                        <p className="font-heading font-bold text-lg">
                            {new Date(event.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">
                            <Clock size={14} /> Time
                        </div>
                        <p className="font-heading font-bold text-lg">
                            {new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                    </div>

                    <div>
                        <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">
                            <MapPin size={14} /> Location
                        </div>
                        <p className="font-heading font-bold text-lg leading-tight">
                            {event.location}
                        </p>
                         <a href={`https://maps.google.com/?q=${event.location}`} target="_blank" rel="noreferrer" className="text-xs underline text-gray-500 hover:text-black mt-1 block">
                            Get Directions
                        </a>
                    </div>
                </div>

                {event.registrationUrl && (
                    <a 
                        href={event.registrationUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="block w-full bg-black text-white text-center py-4 mt-8 font-heading font-bold uppercase tracking-widest hover:bg-brand-gold transition-colors"
                    >
                        Register Now
                    </a>
                )}
            </div>

             <button className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-black transition-colors w-full justify-center border border-gray-200 py-3">
                <Share2 size={14} /> Share Event
            </button>
        </div>

        {/* Right: Description */}
        <div className="md:col-span-2">
            <h3 className="font-heading font-bold text-2xl uppercase mb-6">About this Event</h3>
             <div className="font-body leading-relaxed text-gray-700 whitespace-pre-wrap text-lg">
                {event.description}
            </div>
        </div>

      </div>

      {/* Footer Navigation */}
      <div className="max-w-4xl mx-auto px-4 mt-16 pt-8 border-t border-gray-200">
        <button onClick={() => navigate('/events')} className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:text-gray-500 transition-colors">
            <ArrowLeft size={16} /> Back to Events
        </button>
      </div>

    </article>
  );
};

export default EventDetail;
